<template>
  <div
    class="c-trailer"
    :class="[{ 'is-playing': isPlaying }, mode && `c-trailer--${mode}`]"
  >
    <!-- Android Webview requires poster image, otherwise throws error destroying player -->
    <video
      v-if="hasVideo"
      ref="video"
      class="c-trailer__video"
      :class="{ 'c-trailer__video--opacity': isHeroMode }"
      poster="/static/images/fake-android-webview-poster.gif"
      playsinline
    ></video>
    <div
      class="c-trailer__container"
      :class="{
        'c-trailer__image--zoom': isTileMode && !isHeroMode,
        'c-trailer__image--zoom-with-opacity': isHeroMode,
      }"
    >
      <v-product-image
        :product="product"
        :type="imageType"
        class="c-trailer__image"
      ></v-product-image>
    </div>
    <template v-if="hasVideo && !isPreview">
      <div class="c-trailer__gradient"></div>
      <div class="c-trailer__footer">
        <span
          v-if="!isHeroOrTileMode && product.trailer"
          class="o-typography__label u-uppercase"
        >
          {{ $t('glossary.trailer') }}
        </span>
        <i
          v-if="!isHeroOrTileMode"
          class="o-icon o-icon--trailer-buttons o-typography--action"
          :class="{ 'o-icon--play': !isPlaying, 'o-icon--pause': isPlaying }"
          @click.prevent="togglePlay"
        ></i>
        <i
          v-if="!isHeroOrTileMode || isPlaying"
          class="o-icon o-icon--trailer-buttons o-typography--action"
          :class="{ 'o-icon--sound': !isMuted, 'o-icon--sound-off': isMuted }"
          @click.prevent="toggleMute"
        ></i>
      </div>
    </template>
  </div>
</template>
<script>
  import axios from 'axios';
  import { SDK } from '@redge-media/fo-js-sdk';
  import { EventType, ImageType, ProductType, TrailerMode } from '@/shared/enums';
  import { ADULT_RATING_AGE } from '@/shared/utils';
  import { VideoType } from '@/shared/libs/player';
  import { isObjectEmpty } from '@/shared/libs/helpers';
  import VProductImage from '../product-image/index.vue';

  export default {
    components: {
      VProductImage,
    },
    props: {
      delay: {
        type: Number,
        default: 2000,
      },
      imageType: {
        type: String,
        default: ImageType.COVER,
      },
      mode: String,
      product: Object,
    },
    data() {
      return {
        isMuted: true,
        isPlaying: false,
        timeout: null,
        file: null,
        source: null,
      };
    },
    computed: {
      isBanner() {
        return this.product.type === ProductType.BANNER;
      },
      isPreview() {
        if (this.product.rating && this.product.rating >= ADULT_RATING_AGE) {
          return false;
        }

        return this.product.previewUrl && !this.product.trailer;
      },
      isHeroMode() {
        return this.mode === TrailerMode.HERO;
      },
      isHeroOrTileMode() {
        return this.isHeroMode || this.isTileMode;
      },
      isTileMode() {
        return this.mode === TrailerMode.TILE;
      },
      hasVideo() {
        return this.product.trailer || this.isPreview || this.product.videos;
      },
      hasFile() {
        return this.file && !isObjectEmpty(this.file);
      },
    },
    beforeDestroy() {
      this.stop();
    },
    methods: {
      play(delay = this.delay) {
        if (!this.hasVideo) {
          return;
        }

        this.stop();

        this.timeout = setTimeout(() => {
          this.getPlaylist().then(async () => {
            if (!this.hasFile) {
              return;
            }

            this.$EventBus.on(EventType.RESIZE, this.fitVideo);

            this.$root.redcdnplayer = await this.$redGalaxyPlayer.createInstance(this.$refs.video);

            this.$root.redcdnplayer.on('metadata', () => {
              this.fitVideo();
            });

            this.$root.redcdnplayer.on('playing', () => {
              this.isPlaying = true;
            });

            this.$root.redcdnplayer.on('paused', () => {
              this.isPlaying = false;
            });

            this.$root.redcdnplayer.on('volumeChanged', (volume) => {
              this.isMuted = volume === 0;
            });

            this.$root.redcdnplayer.on('ended', () => {
              this.stop();
              this.$emit('ended');
            });

            this.$root.redcdnplayer.on('destroy', () => {
              this.isPlaying = false;
              this.isMuted = true;
            });

            this.$root.redcdnplayer.setup({
              file: this.file,
              volume: 0,
              disableNativeControls: true,
              volumeLocalStorageEnabled: false,
            });

            this.$root.redcdnplayer.load();
          }).catch(() => {});
        }, delay);
      },
      stop() {
        clearTimeout(this.timeout);

        if (this.source) {
          this.source.cancel();
          this.source = null;
        }

        this.$EventBus.off(EventType.RESIZE, this.fitVideo);

        if (!this.$root.redcdnplayer) {
          return;
        }

        this.$root.redcdnplayer.destroy();
        this.$root.redcdnplayer = null;
      },
      getPlaylist() {
        if (this.file) {
          return Promise.resolve();
        }

        if (this.isPreview) {
          return this.getPreviewPlaylist();
        }

        if (this.isBanner) {
          return this.getBannerPlaylist();
        }

        return this.getTrailerPlaylist();
      },
      getPreviewPlaylist() {
        this.source = axios.CancelToken.source();
        const cancelToken = this.source.token;

        return axios.get(this.product.previewUrl, { cancelToken })
          .then((response) => {
            if (!response.data.preview.path) {
              return;
            }

            const fileUrl = this.product.previewUrl.replace('preview.json', response.data.preview.path);
            this.file = { mp4: fileUrl };
          });
      },
      getBannerPlaylist() {
        const file = {};

        this.product.videos.forEach((video) => {
          file[video.format.toLowerCase()] = video.url;
        });

        this.file = file;
        return Promise.resolve();
      },
      getTrailerPlaylist() {
        this.source = SDK.apiAdapter.getSource();
        const cancelToken = this.source.token;

        return SDK.apiAdapter.get(`products/${this.product.id}/videos/playlist`, { videoType: VideoType.TRAILER }, { cancelToken })
          .then((response) => {
            const file = {};
            const sources = response.data.sources;

            ['DASH', 'HLS', 'SS', 'MP4'].forEach((type) => {
              if (sources[type] && sources[type][0] && sources[type][0].src) {
                file[type.toLowerCase()] = sources[type][0].src;
              }
            });

            this.file = file;
          });
      },
      toggleMute() {
        if (!this.$root.redcdnplayer) {
          return;
        }

        this.isMuted ? this.$root.redcdnplayer.setVolume(100) : this.$root.redcdnplayer.setVolume(0);
      },
      togglePlay() {
        if (!this.$root.redcdnplayer) {
          this.play(0);
          return;
        }

        this.isPlaying ? this.$root.redcdnplayer.pause() : this.$root.redcdnplayer.play();
      },
      fitVideo() {
        // TODO Check object-fit support by browser
        const { clientHeight: containerHeight, clientWidth: containerWidth } = this.$el;
        const video = this.$refs.video;
        const ratio = video.videoWidth / video.videoHeight;

        if (isNaN(ratio)) {
          return;
        }

        let videoHeight;
        let videoWidth;
        let videoTop;
        let videoLeft;

        if (containerHeight * ratio < containerWidth) {
          videoHeight = containerWidth / ratio;
          videoWidth = containerWidth;
          videoTop = (containerHeight - videoHeight) / 2;
          videoLeft = 0;
        } else {
          videoHeight = containerHeight;
          videoWidth = containerHeight * ratio;
          videoTop = 0;
          videoLeft = (containerWidth - videoWidth) / 2;
        }

        video.style.height = videoHeight + 'px';
        video.style.width = videoWidth + 'px';
        video.style.top = videoTop + 'px';
        video.style.left = videoLeft + 'px';
      },
    },
  };
</script>
